import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import useCanonicalUrl from '../hooks/useCanonicalUrl'
import useFAQSchema from '../hooks/useFAQSchema'
import ToolPageTemplate from '../templates/ToolPageTemplate'
import Input from '../atoms/Input'
import Button from '../atoms/Button'
import ResultBox from '../organisms/ResultBox'
import { setHijriBirthDate, calculateHijriAge, clearHijriAge } from '../store/slices/ageSlice'
import '../pages/AgePage.css'

const IslamicAgeCalculatorPage = ({ tools = [] }) => {
    useCanonicalUrl()
    useFAQSchema()
    const dispatch = useDispatch()
    const { hijriBirthDate, hijriResult } = useSelector(state => state.age)
    const [lastUpdated, setLastUpdated] = useState(null)

    useEffect(() => {
        setLastUpdated(
            new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
        )
    }, [])

    useEffect(() => {
        document.title = 'Islamic Age Calculator - Hijri Calendar Age Converter | EveryKits'
        const metaDescription = document.querySelector('meta[name="description"]')
        if (metaDescription) {
            metaDescription.setAttribute('content', 'Calculate your age in the Islamic Hijri calendar from your Gregorian date of birth. Free and accurate Hijri age converter.')
        }
        const structuredData = {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Islamic Age Calculator",
            "applicationCategory": "WebApplication",
            "operatingSystem": "All",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
            "description": "Free Islamic Hijri age calculator converting Gregorian birth date to Hijri age.",
            "url": "https://everykits.com/islamic-age-calculator/"
        }
        document.querySelectorAll('script[data-schema="software-application"]').forEach(el => el.remove());
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-schema', 'software-application');
        script.textContent = JSON.stringify(structuredData)
        document.head.appendChild(script)
        return () => {
            if (document.head.contains(script)) {
                document.head.removeChild(script);
            }
        };
    }, [])

    return (
        <ToolPageTemplate
            title="Islamic Age Calculator"
            description="Calculate your age in the Hijri (Islamic) calendar"
            tools={tools}
            breadcrumbItems={[
                { name: 'Home', path: '/' },
                { name: 'Age Calculator', path: '/age/' },
                { name: 'Islamic Age Calculator', path: '/islamic-age-calculator/' },
            ]}
        >
            <div>
                <section className="age-calculator">
                    <div className="age-input-section">
                        <Input
                            type="date"
                            label="Date of Birth (Gregorian)"
                            value={hijriBirthDate}
                            onChange={(e) => dispatch(setHijriBirthDate(e.target.value))}
                            max={new Date().toISOString().split('T')[0]}
                        />
                        <div className="age-buttons">
                            <Button onClick={() => dispatch(calculateHijriAge())} disabled={!hijriBirthDate}>Calculate Islamic Age</Button>
                            <Button variant="secondary" onClick={() => dispatch(clearHijriAge())}>Clear</Button>
                        </div>
                    </div>

                    {hijriResult && (
                        <ResultBox title="Age in Islamic (Hijri) Calendar — approximate">
                            <div className="age-details">
                                <div className="age-item">
                                    <span className="age-label">Hijri Years:</span>
                                    <span className="age-value">{hijriResult.hijriYears}</span>
                                </div>
                                <div className="age-item">
                                    <span className="age-label">Hijri Months:</span>
                                    <span className="age-value">{hijriResult.hijriMonths}</span>
                                </div>
                                <div className="age-item">
                                    <span className="age-label">Hijri Days:</span>
                                    <span className="age-value">{hijriResult.hijriDays}</span>
                                </div>
                                <div className="age-item">
                                    <span className="age-label">Birth Date (Hijri):</span>
                                    <span className="age-value">{hijriResult.birthHijri.year}-{hijriResult.birthHijri.month}-{hijriResult.birthHijri.day}</span>
                                </div>
                            </div>
                        </ResultBox>
                    )}
                </section>

                <section className="tool-guide">
                    <div className="guide-section">
                        <h2>How to Use Islamic Age Calculator</h2>
                        <div className="guide-steps">
                            <div className="step">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <h3>Enter Gregorian Birth Date</h3>
                                    <p>Select your date of birth as recorded in the standard Gregorian calendar.</p>
                                </div>
                            </div>
                            <div className="step">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <h3>Calculate</h3>
                                    <p>Click calculate to instantly convert your age into the Hijri (Islamic lunar) calendar system.</p>
                                </div>
                            </div>
                            <div className="step">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <h3>View Hijri Age & Birth Date</h3>
                                    <p>See your age in Hijri years, months, and days, along with your birth date converted to the Hijri format.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="guide-section">
                        <h2>Why Use This Tool</h2>
                        <p>The Islamic Hijri calendar is lunar-based, running about 11 days shorter per year than the Gregorian solar calendar — meaning your Hijri age differs meaningfully from your Gregorian age over time. This is essential for religious observances, Islamic documentation, and cultural context, and our calculator handles the complex lunar-to-solar conversion instantly and accurately.</p>
                    </div>
                    <div className="guide-section">
                        <h2>Frequently Asked Questions</h2>
                        <div className="faq-list">
                            <div className="faq-item">
                                <h3>How accurate is the Hijri conversion?</h3>
                                <p>Our calculator uses standard astronomical Hijri calendar formulas for a close approximation—actual moon-sighting based dates may vary by 1-2 days.</p>
                            </div>
                            <div className="faq-item">
                                <h3>Why is a Hijri year shorter than a Gregorian year?</h3>
                                <p>The Hijri calendar is lunar-based, with about 354 days per year, roughly 11 days shorter than the 365-day Gregorian solar calendar.</p>
                            </div>
                            <div className="faq-item">
                                <h3>Can I convert my birth date to Hijri format?</h3>
                                <p>Yes, the calculator shows both your current Hijri age and your birth date converted into the Hijri calendar format.</p>
                            </div>
                        </div>
                    </div>
                </section>
                <section className="tool-guide">
                    <div className="guide-section">
                        <h2>Related Tools</h2>
                        <div className="related-tools">
                            <Link to="/age/" className="related-tool">
                                <div className="tool-icon">Age Calculator</div>
                                <div className="tool-description">Calculate your exact age from date of birth</div>
                            </Link>
                            <Link to="/eligibility-calculator/" className="related-tool">
                                <div className="tool-icon">Eligibility Calculator</div>
                                <div className="tool-description">Check age eligibility for exams and life milestones</div>
                            </Link>
                            <Link to="/blog/understanding-the-islamic-hijri-calendar/" className="related-tool">
                                <div className="tool-icon"> Understanding the Islamic Hijri Calendar</div>
                                <div className="tool-description">A deeper look at how the lunar calendar works</div>
                            </Link>
                        </div>
                    </div>
                </section>

                <div className="last-updated">
                    <p suppressHydrationWarning>Last updated: {lastUpdated || 'Recently'}</p>
                </div>
            </div>
        </ToolPageTemplate>
    )
}

export default IslamicAgeCalculatorPage